<template>
  <div class="is-height-100p">
    <div>
      <AppHeader :urlLogoSociete="urlLogoSociete"/>
    </div>
    <b-autocomplete
      v-model="search"
      :data="filtreRecoms"
      placeholder="Recherche un point de vente"
      icon="magnify"
      clearable
      field="libelle"
      @select="accessRecomPdv($event)">
      <template slot="empty">Aucune recommandation trouvée</template>
    </b-autocomplete>
    <CartoGeneral :pointDeVentes="pdvFilter" :panneaux="panneauxFilter"/>
    <ListeEnseigne
      :recoms="recomsFilter"
      :enseignes="enseignes"
      v-if="typeClient === 'Groupe' && currentTypeClient === 'Groupe'"
      @go-to-list-enseigne="goToListEnseigne"/>
    <ListePointDeVente
      :recoms="recomsFilter"
      :enseignes="enseignesFilter"
      :pdVentes="pdvFilter"
      :typeClient="typeClient"
      v-if="currentTypeClient === 'Enseigne'"
      @back="currentTypeClient = 'Groupe'"/>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import gestionErreur from '@/services/general/gestionErreur';
import Header from '@/components/general/Header';
import CartoGeneral from '@/components/portailGroupe/CartoGeneralPortail';
import ListeEnseigne from '@/components/portailGroupe/ListeEnseigne';
import ListePointDeVente from '@/components/portailGroupe/ListePointDeVente';
import { getRecomClient, getEnseignes, GetPiecesJointesAsync } from '@/api/recom/index';
import { iconPointDeVente } from '@/services/gestionIconMarker/iconColorGetter';
import userConfig from '@/configurations/userConfig';

const User = createNamespacedHelpers('user');

export default {
  components: {
    AppHeader: Header,
    CartoGeneral,
    ListeEnseigne,
    ListePointDeVente,
  },
  data: () => ({
    search: '',
    recoms: [],
    typeClient: '',
    currentTypeClient: '',
    idClient: '',
    pdv: [],
    panneaux: [],
    enseignes: [],
    urlLogoSociete: '',
  }),
  computed: {
    recomsFilter() {
      if (this.currentTypeClient === 'Enseigne') {
        return this.recoms.filter(recom => recom.data.pointDeVentes[0].idEnseigne === this.idClient);
      }
      return this.recoms;
    },
    filtreRecoms() {
      return this.recomsFilter
        .map(recom => ({
          libelle: recom.data.pointDeVentes[0].libelle,
          previewLink: recom.previewLink,
        }))
        .filter((pdv) => {
        return pdv.libelle
            .toString()
            .toLowerCase()
            .indexOf(this.search.toLowerCase()) >= 0
      });
    },
    pdvFilter() {
      if (this.currentTypeClient === 'Enseigne') {
        return this.pdv.filter(p => p.idEnseigne === this.idClient);
      }
      return this.pdv;
    },
    panneauxFilter() {
      if (this.currentTypeClient === 'Enseigne') {
        return this.panneaux.filter(p => p.idEnseigne === this.idClient);
      }
      return this.panneaux;
    },
    enseignesFilter() {
      if (this.currentTypeClient === 'Enseigne') {
        return this.enseignes.filter(p => p.num === this.idClient);
      }
      return this.enseignes;
    }
  },
  methods: {
    ...User.mapActions({
      logIn: 'logIn',
    }),
    async load() {
      const loadingComponent = this.$buefy.loading.open({ container: null });
      try {
        await this.logIn(userConfig);
        const res = (await getRecomClient(this.$route.params.hash)).data;
        this.recoms = res.recoms;
        this.typeClient = this.currentTypeClient = res.typeClient;
        this.idClient = res.id;
        this.urlLogoSociete = res.societe?.urlLogo;
        await this.genererPointDeVente();
        await this.genererPanneaux();
        await this.genererEnseignes();
        loadingComponent.close();
      } catch (e) {
        loadingComponent.close();
        gestionErreur('Erreur serveur');
      }
    },
    accessRecomPdv(data){
      window.open(data.previewLink);
    },
    generateIconPdv(url) {
      return new Promise(resolve => {
        const img = document.createElement('img');
        img.onload = () => {
          const logo = iconPointDeVente(img);
          const icon = {
            url: logo,
            scaledSize: new google.maps.Size(28, 33),
            origin: new google.maps.Point(0, 0),
            anchor: new google.maps.Point(28 / 2, 33),
          };
          resolve(icon);
        };
        img.onerror = () => {
          img.src = require('@/assets/images/default_img.png');
        };
        img.setAttribute('src', url);
        img.setAttribute('crossOrigin', 'Anonymous');
      })
    },
    async genererPointDeVente() {
      const pdv = this.recomsFilter
        .map(recom => ({
          adresse: recom.data.pointDeVentes[0].adresse,
          libelle: recom.data.pointDeVentes[0].libelle,
          srcImg: recom.data.pointDeVentes[0].srcImg,
          icon: recom.data.pointDeVentes[0].icon,
          idEnseigne: recom.data.pointDeVentes[0].idEnseigne,
          previewLink: recom.previewLink,
          montant: recom.data.montant,
          nbrePanneauxNew: recom.data.pointDeVentes[0].panneaux.filter(pan => pan.ancienPanneau.toUpperCase() === 'NON').length,
          nbrePanneauxOld: recom.data.pointDeVentes[0].panneaux.filter(pan => pan.ancienPanneau.toUpperCase() === 'OUI').length,
          nbrePanneaux: recom.data.pointDeVentes[0].panneaux.length
        })).sort((a, b) => a.libelle.localeCompare(b.nom));
      const imgs = [... new Set(pdv.map(el => el.srcImg))].filter(el => el !== null && el.includes('https://'));
      let req = [];
      let urls = imgs.map(el => ({ url: el}));
      urls.forEach(el => {
        req.push(this.generateIconPdv(el.url))
      });
      let res = await Promise.all(req);
      urls.forEach((el, i) => {
        el.img = res[i];
      });
      req = res = [];
      pdv.forEach(el => {
        const url = urls.find(val => val.url === el.srcImg);
        if (url) el.icon = url.img;
      });
      this.pdv = pdv;
    },
    async genererPanneaux() {
      const pdvs = this.recomsFilter
        .map(recom => recom.data.pointDeVentes[0]);
      pdvs.forEach(pdv => {
        pdv.panneaux.forEach(pann => {
          pann.idEnseigne = pdv.idEnseigne;
        });
      });
      const panneaux = pdvs.map(pdv => pdv.panneaux).flat()
        .map(pann => ({
          adresse: pann.adresse,
          couleur: pann.categorie.couleur,
          azimut: pann.azimut,
          idEnseigne: pann.idEnseigne,
          ancienPanneau: pann.ancienPanneau
        })).filter(pann => pann.adresse.coordonnees.lng > -5 && pann.adresse.coordonnees.lng < 12 && pann.adresse.coordonnees.lat > 40);
      this.panneaux = panneaux;
    },
    async genererEnseignes() {
      const loadingComponent = this.$buefy.loading.open({ container: this.$refs.pageEnseigne });
      const idEnseignes = [... new Set(this.recomsFilter.map(recom => recom.data.pointDeVentes[0].idEnseigne))];
      const req = [];
      idEnseignes.forEach(id => {
        req.push(GetPiecesJointesAsync(id));
      });
      const response = await Promise.all(req);
      this.enseignes = (await getEnseignes(idEnseignes)).data;
      this.enseignes.forEach((el, i) => {
        el.urlLogo = response[i].data.chemin;
      });
      loadingComponent.close();
    },
    goToListEnseigne(idEnseigne) {
      this.idClient = idEnseigne;
      this.currentTypeClient = 'Enseigne';
    }
  },
  mounted() {
    this.load();
  },
}
</script>

<style>

</style>